/**
 * UI primitives.
 *
 * A small hand-rolled set rather than a component library, for two reasons: this app's
 * needs are narrow (dense tables, badges, a few buttons), and the verdict components
 * below encode a safety rule that a generic `<Badge>` would let a future contributor
 * bypass. Keeping them here makes the rule structural.
 */
import { clsx } from 'clsx'
import {
  AlertTriangle,
  Check,
  CircleHelp,
  Info,
  Loader2,
  X,
  type LucideIcon,
} from 'lucide-react'
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react'
import type { MatchBand, Verdict } from '@rescure/shared'

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 border-transparent',
  secondary:
    'bg-[var(--surface-card)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)] border-[var(--border-strong)]',
  ghost: 'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] border-transparent',
  danger: 'bg-unmet-600 text-white hover:bg-unmet-700 border-transparent',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Shows a spinner and disables the button. */
  loading?: boolean
  icon?: LucideIcon
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon: Icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      // A loading button must be un-clickable, not merely spinning: the common bug is a
      // double-submitted state transition that fails the second time with a confusing
      // "illegal transition" error.
      disabled={disabled || loading}
      // `aria-busy` so a screen reader announces the pending state, which the spinner
      // alone does not convey.
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-md border font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-55',
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
        BUTTON_VARIANTS[variant],
        className
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 aria-hidden className="size-3.5 animate-spin" />
      ) : Icon ? (
        <Icon aria-hidden className="size-3.5" />
      ) : null}
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'rounded-card border bg-[var(--surface-card)] shadow-card',
        'border-[var(--border-subtle)]',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

export function CardHeader({
  title,
  subtitle,
  actions,
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div className={clsx('flex items-start justify-between gap-3 border-b border-[var(--border-subtle)] px-4 py-3', className)}>
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold text-[var(--text-primary)]">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Verdict and band badges — colour is never the only signal
// ---------------------------------------------------------------------------

/**
 * Presentation for each verdict.
 *
 * Every entry pairs a colour with an icon *and* a word. That is not belt-and-braces
 * decoration: roughly 1 in 12 men has a colour-vision deficiency, and in this product
 * mistaking "not met" for "met" is a clinical error. The icon and the label are what
 * carry the meaning; the colour only makes it faster to scan.
 *
 * `INSUFFICIENT_INFORMATION` reads as "Needs a check" rather than "Unknown", because it
 * is a work item — a lab to order, a question to ask at the visit — and the copy should
 * prompt the action rather than merely report an absence.
 */
const VERDICT_PRESENTATION: Record<Verdict, { label: string; icon: LucideIcon; className: string }> = {
  MET: {
    label: 'Met',
    icon: Check,
    className: 'bg-met-50 text-met-700 border-met-500/35',
  },
  NOT_MET: {
    label: 'Not met',
    icon: X,
    className: 'bg-unmet-50 text-unmet-700 border-unmet-500/35',
  },
  INSUFFICIENT_INFORMATION: {
    label: 'Needs a check',
    icon: CircleHelp,
    className: 'bg-unknown-50 text-unknown-700 border-unknown-500/40',
  },
  NOT_APPLICABLE: {
    label: 'Not applicable',
    icon: Info,
    className: 'bg-[var(--surface-sunken)] text-[var(--text-muted)] border-[var(--border-subtle)]',
  },
}

export function VerdictBadge({ verdict, className }: { verdict: Verdict; className?: string }) {
  const { label, icon: Icon, className: tone } = VERDICT_PRESENTATION[verdict]
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-2xs font-medium whitespace-nowrap',
        tone,
        className
      )}
    >
      <Icon aria-hidden className="size-3" />
      {label}
    </span>
  )
}

/**
 * Band presentation.
 *
 * Note that the *patient-facing* wording lives in `PATIENT_BAND_COPY` in
 * `@rescure/shared`, not here. This component's labels are for clinicians and
 * coordinators; showing a patient "Unlikely fit" in this register would be needlessly
 * cold, and showing them a percentage would be false precision. Keeping the two
 * vocabularies apart is deliberate — see DECISIONS.md §7.
 */
const BAND_PRESENTATION: Record<MatchBand, { label: string; className: string }> = {
  LIKELY_FIT: { label: 'Likely fit', className: 'bg-met-100 text-met-700 border-met-500/40' },
  POSSIBLE_FIT: { label: 'Possible fit', className: 'bg-unknown-100 text-unknown-700 border-unknown-500/40' },
  UNLIKELY_FIT: { label: 'Unlikely', className: 'bg-[var(--surface-sunken)] text-[var(--text-secondary)] border-[var(--border-strong)]' },
  NOT_ELIGIBLE: { label: 'Not eligible', className: 'bg-unmet-100 text-unmet-700 border-unmet-500/40' },
}

export function BandBadge({ band, className }: { band: MatchBand; className?: string }) {
  const { label, className: tone } = BAND_PRESENTATION[band]
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded border px-1.5 py-0.5 text-2xs font-semibold whitespace-nowrap',
        tone,
        className
      )}
    >
      {label}
    </span>
  )
}

/** Neutral label chip, for phases, states, and other metadata. */
export function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded border border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-1.5 py-0.5 text-2xs text-[var(--text-secondary)] whitespace-nowrap',
        className
      )}
    >
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Probability display
// ---------------------------------------------------------------------------

/**
 * The matcher's fit score, shown only to clinical roles.
 *
 * Rendered as a score out of 100, not a percentage, and captioned as what it is: the
 * deterministic rule formula in `functions/_lib/matcher/score.ts` (term overlap, share of
 * checkable criteria met, age/sex fit). It ranks matches well; it has not been calibrated
 * against enrolment outcomes, so it must not read as "N% chance of eligibility".
 */
export function ProbabilityMeter({
  probability,
  showCaption = false,
  className,
}: {
  probability: number
  showCaption?: boolean
  className?: string
}) {
  const percent = Math.round(probability * 100)
  const tone = probability >= 0.65 ? 'bg-met-500' : probability >= 0.2 ? 'bg-unknown-500' : 'bg-ink-400'

  return (
    <div className={clsx('min-w-24', className)}>
      <div className="flex items-baseline gap-1.5">
        <span className="tabular text-sm font-semibold text-[var(--text-primary)]">{percent}</span>
        <div
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--surface-sunken)]"
          role="img"
          aria-label={`Fit score ${percent} out of 100`}
        >
          <div className={clsx('h-full rounded-full', tone)} style={{ width: `${Math.max(2, percent)}%` }} />
        </div>
      </div>
      {showCaption ? (
        <p className="mt-1 text-2xs text-[var(--text-muted)]">
          Rule-based fit score out of 100, for ranking matches. Not a calibrated probability of eligibility.
        </p>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export function Spinner({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div className={clsx('flex items-center gap-2 text-sm text-[var(--text-secondary)]', className)} role="status">
      <Loader2 aria-hidden className="size-4 animate-spin" />
      <span>{label}</span>
    </div>
  )
}

/**
 * Empty state.
 *
 * `hint` is not optional in practice — an empty screen that does not say what to do next
 * is the most common way a demo stalls in front of an audience. Callers are nudged to
 * explain the next action.
 */
export function EmptyState({
  title,
  hint,
  icon: Icon = Info,
  action,
}: {
  title: string
  hint?: ReactNode
  icon?: LucideIcon
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <Icon aria-hidden className="size-6 text-[var(--text-muted)]" />
      <p className="text-sm font-medium text-[var(--text-primary)]">{title}</p>
      {hint ? <p className="max-w-md text-xs text-[var(--text-secondary)]">{hint}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}

type AlertTone = 'info' | 'warning' | 'error' | 'success'

const ALERT_TONES: Record<AlertTone, { className: string; icon: LucideIcon }> = {
  info: { className: 'bg-brand-50 text-brand-800 border-brand-200', icon: Info },
  warning: { className: 'bg-unknown-50 text-unknown-700 border-unknown-500/40', icon: AlertTriangle },
  error: { className: 'bg-unmet-50 text-unmet-700 border-unmet-500/40', icon: AlertTriangle },
  success: { className: 'bg-met-50 text-met-700 border-met-500/40', icon: Check },
}

export function Alert({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: AlertTone
  title?: ReactNode
  children?: ReactNode
  action?: ReactNode
}) {
  const { className, icon: Icon } = ALERT_TONES[tone]
  return (
    <div
      className={clsx('flex items-start gap-2 rounded-md border px-3 py-2 text-xs', className)}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <Icon aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={clsx(title && 'mt-0.5')}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

/** Loading placeholder sized to the content it stands in for. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('animate-pulse rounded bg-[var(--surface-sunken)]', className)} aria-hidden />
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  // Horizontal scroll is contained here so a wide criteria table never makes the whole
  // page scroll sideways on a phone.
  return (
    <div className="overflow-x-auto">
      <table className={clsx('w-full border-collapse text-sm', className)}>{children}</table>
    </div>
  )
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={clsx(
        'border-b border-[var(--border-subtle)] px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wide text-[var(--text-muted)]',
        className
      )}
    >
      {children}
    </th>
  )
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <td className={clsx('border-b border-[var(--border-subtle)] px-3 py-2 align-top', className)}>{children}</td>
  )
}

/**
 * A field that is missing rather than empty.
 *
 * Used everywhere a value could be absent, so that "no data" never renders as a blank
 * cell or a zero. A blank cell is ambiguous between "not applicable", "zero", and "we
 * failed to load it", and in a clinical table that ambiguity is exactly the kind that
 * leads someone to assume the reassuring interpretation.
 */
export function NotOnFile({ what }: { what?: string }) {
  return (
    <span className="text-xs italic text-[var(--text-muted)]">
      {what ? `${what} not on file` : 'Not on file'}
    </span>
  )
}
